
export async function extractTextFromPDF(pdfDoc) {
    const pagesText = [];
    for (let i = 1; i <= pdfDoc.numPages; i++) {
        const page = await pdfDoc.getPage(i);
        const textContent = await page.getTextContent();
        const text = textContent.items.map((item) => item.str).join(" ");
        pagesText.push({ pageNum: i, text, items: textContent.items });
    }
    return pagesText;
}



// ── Spelling suggestions ──────────────────────────────────────────────────────
// Suggestions are built from the words of the CURRENTLY OPEN pdf, not a shipped
// dictionary. That keeps it language-agnostic (works for Hindi + English + proper
// nouns) and guarantees a suggestion actually exists in the document.

// Unicode-aware: \p{M} keeps Devanagari matras attached to their base letter.
const TOKEN_RE = /[\p{L}\p{N}][\p{L}\p{N}\p{M}]*/gu;
const DEVANAGARI_RE = /[ऀ-ॿ]/;

function normalizeWord(word) {
    // NFC so composed/decomposed Devanagari don't become two separate entries.
    return word.normalize("NFC").toLowerCase();
}

export function buildVocabulary(pagesText) {
    const vocabulary = new Map();
    pagesText.forEach(({ text }) => {
        const tokens = text.match(TOKEN_RE);
        if (!tokens) return;
        for (const token of tokens) {
            if (token.length < 3) continue;
            const word = normalizeWord(token);
            vocabulary.set(word, (vocabulary.get(word) || 0) + 1);
        }
    });
    return vocabulary;
}

// Banded Levenshtein over code points. Returns -1 as soon as the distance is
// known to exceed maxDist, so most candidates bail out after a row or two.
function levenshteinWithin(a, b, maxDist) {
    const al = a.length;
    const bl = b.length;
    if (Math.abs(al - bl) > maxDist) return -1;

    let prev = new Array(bl + 1);
    let curr = new Array(bl + 1);
    for (let j = 0; j <= bl; j++) prev[j] = j;

    for (let i = 1; i <= al; i++) {
        const lo = Math.max(1, i - maxDist);
        const hi = Math.min(bl, i + maxDist);
        curr[0] = i;
        if (lo > 1) curr[lo - 1] = Infinity;

        let rowMin = lo > 1 ? Infinity : curr[0];
        for (let j = lo; j <= hi; j++) {
            const cost = a[i - 1] === b[j - 1] ? 0 : 1;
            curr[j] = Math.min(prev[j] + 1, curr[j - 1] + 1, prev[j - 1] + cost);
            if (curr[j] < rowMin) rowMin = curr[j];
        }
        if (hi < bl) curr[hi + 1] = Infinity;
        if (rowMin > maxDist) return -1;

        const swap = prev;
        prev = curr;
        curr = swap;
    }

    return prev[bl] <= maxDist ? prev[bl] : -1;
}

// Closest word in the document to `query`, or null if nothing is close enough.
// Multi-word queries are skipped — the tokenizer only knows single words.
export function suggestWord(vocabulary, query) {
    if (!vocabulary || vocabulary.size === 0) return null;

    const q = normalizeWord((query || "").trim());
    if (q.length < 3 || /\s/.test(q) || vocabulary.has(q)) return null;

    const queryCp = Array.from(q);
    const len = queryCp.length;
    let maxDist = len <= 4 ? 1 : len <= 8 ? 2 : 3;
    // One wrong matra shifts meaning more than one wrong Latin letter does.
    if (DEVANAGARI_RE.test(q)) maxDist = Math.min(maxDist, len <= 4 ? 1 : 2);

    let best = null;
    for (const [word, count] of vocabulary) {
        // Loose prefilter: UTF-16 length overcounts combining marks, so allow slack.
        if (Math.abs(word.length - q.length) > maxDist * 2) continue;

        const dist = levenshteinWithin(queryCp, Array.from(word), maxDist);
        if (dist < 0) continue;

        if (!best || dist < best.dist || (dist === best.dist && count > best.count)) {
            best = { word, dist, count };
        }
    }

    return best ? best.word : null;
}


// PDF text comes in fragments whose spacing is unreliable: extra spaces between
// items, words split across items, or no space at all where the layout implies
// one. So matching ignores whitespace on both sides — "LOKPAL OF INDIA" finds
// "LOKPAL  OF INDIA", "LOKPALOF INDIA", "LOK PAL OF INDIA", etc.
// Returns [{ start, end }] as offsets into the ORIGINAL text (end exclusive).
// Shared by the match counter and the on-page highlighter so their counts agree.
export function findMatchesIgnoringSpaces(text, query) {
    const q = (query || "").replace(/\s+/g, "");
    if (q.length < 2 || !text) return [];

    let compact = "";
    const map = []; // compact index → original index
    for (let i = 0; i < text.length; i++) {
        if (/\s/.test(text[i])) continue;
        compact += text[i];
        map.push(i);
    }

    const regex = new RegExp(q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "gi");
    const matches = [];
    let match;
    while ((match = regex.exec(compact)) !== null) {
        const last = match.index + match[0].length - 1;
        matches.push({ start: map[match.index], end: map[last] + 1 });
    }
    return matches;
}

export function searchInText(pagesText, query) {
    if (!query || query.trim().length < 2) return [];
    const matches = [];

    pagesText.forEach(({ pageNum, text }) => {
        for (const { start, end } of findMatchesIgnoringSpaces(text, query)) {
            matches.push({ pageNum, start, end, text: text.slice(start, end) });
        }
    });
    return matches;
}
